import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';
import usePublicPostApi from '~/public/lib/usePublicPostApi.js';
import useSharedPostApi from '~/lib/useSharedPostApi.js';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { getCurrencyCode } from '~/lib/CurrencyHelpers.js';
import { isAdminApp } from '~/public/lib/PageHelpers.js';

const SquarePaymentForm = ({
    type,
    id,
    paymentSettings,
    paymentBtnLabel,
    paymentElementContainerClassName,
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const { save, isSaving } = useSharedPostApi(
        `/payments/payment-intent/${paymentSettings.paymentIntent.intentToken}`,
    );

    function onTokenized(token, verifiedBuyer) {
        setErrorMessage(null); // Clear any previous errors

        save({
            paymentToken: token,
        }).then(result => {
            const url = new URL(paymentSettings.returnUrl);
            url.searchParams.append('payment_intent', result.token);
            document.location.href = url.toString();
        }).catch(err => {
            const errorMsg = err.response?.data?.message || err.message || 'Payment processing failed. Please try again.';

            // Show error to user
            setErrorMessage(errorMsg);

            // Report Square payment processing error to Sentry
            Sentry.withScope(scope => {
                scope.setContext('square_payment_error', {
                    error_type: 'square_payment_processing_failed',
                    payment_token: token ? '[REDACTED]' : null,
                    verified_buyer: verifiedBuyer ? 'present' : 'absent',
                    application_id: paymentSettings?.paymentIntent?.applicationId,
                    location_id: paymentSettings?.accountId,
                    error_message: errorMsg,
                    status_code: err.response?.status,
                    response_data: err.response?.data,
                });
                scope.setLevel('error');

                Sentry.captureException(err);
            });

            console.error('Square payment processing failed:', err);
        });
        /**
         * Process payment:
         * - Redirect to paymentSettings.returnUrl
         * - pass `payment_intent` as a param
         *
         * maybe locally create a square_payment_intents table?
         *  square_payment_intents:
         *      - id
         *      - amount
         *      - payment_token
         *      - settings
         * that stores the amount intended to pay, then pass this
         * then here we submit the token to be saved with the payment intent
         */
    }


    const cardVerificationDetails = {
        amount: paymentSettings.amount.toString(),
        /* collected from the buyer */
        billingContact: {
            // addressLines: ['123 Main Street', 'Apartment 1'],
            // familyName: 'Doe',
            // givenName: 'John',
            // countryCode: 'GB',
            // city: 'London',
        },
        currencyCode: getCurrencyCode(),
        intent: paymentSettings?.paymentIntent?.vault ? 'CHARGE_AND_STORE' : 'CHARGE',
        customerInitiated: !isAdminApp(),
    };

    return (
        <div className="mt-4">
            {!!errorMessage && (
                <div className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded">
                    {errorMessage}
                </div>
            )}
            {!paymentSettings?.accountId && (
                <div className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded">
                    The business is not set up with Square to process payments. Please log in to your Square account to complete all setup steps and then reconnect Square in Breely.
                </div>
            )}
            {!!paymentSettings?.accountId && (
                <PaymentForm
                    applicationId={paymentSettings?.paymentIntent?.applicationId}
                    cardTokenizeResponseReceived={onTokenized}
                    locationId={paymentSettings?.accountId}
                    createVerificationDetails={() => cardVerificationDetails}
                >
                    <CreditCard>{paymentBtnLabel}</CreditCard>
                </PaymentForm>
            )}

            {!!paymentSettings?.paymentIntent?.vault && (
                <div className="text-gray-500 text-sm mt-2 leading-tight">
                    By providing your card information, you allow your card to
                    be charged for future payments in accordance with payment
                    terms.
                </div>
            )}
        </div>
    );
};

export default SquarePaymentForm;
